/**
 * Global navigation loading bar — the thin gradient bar that sweeps
 * across the very top of the screen during route transitions.
 *
 * Mounted once in AppShell so it's visible on every dashboard tab
 * AND inside /crm. The CRM's internal NavigationLink still dispatches
 * a `nav-start` document event on link click; this bar listens for
 * that and also auto-triggers on any pathname change, so navigation
 * from the dashboard's top tabs gets the same feedback.
 *
 * Lifted from apps/web/src/crm/components/top-loading-bar.tsx so the
 * entire internal system shares one loading indicator. The original
 * used Next.js's `<style jsx>` tag for the shimmer keyframe; we
 * declare it once in globals.css instead so the component stays
 * portable React.
 */
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export function TopLoadingBar(): JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let completeTimer: ReturnType<typeof setTimeout> | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const start = () => {
      setLoading(true);
      setProgress(15);
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 85) return prev;
          const inc = prev < 30 ? Math.random() * 15 : Math.random() * 8;
          return Math.min(prev + inc, 85);
        });
      }, 50);
    };

    const complete = () => {
      if (progressInterval) clearInterval(progressInterval);
      setProgress(100);
      hideTimer = setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 200);
    };

    // Manual trigger: anywhere in the app can dispatch this to show
    // the bar (e.g., before a slow API call). The CRM's NavigationLink
    // still emits this on click.
    document.addEventListener("nav-start", start);

    // Auto-trigger on every pathname change so dashboard top-tab nav
    // shows the bar without needing to wrap NavLink.
    start();
    completeTimer = setTimeout(complete, 200);

    return () => {
      document.removeEventListener("nav-start", start);
      if (progressInterval) clearInterval(progressInterval);
      if (completeTimer) clearTimeout(completeTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [pathname]);

  if (!loading) return null;

  return (
    <Box
      aria-hidden
      sx={{
        pointerEvents: "none",
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        zIndex: 1100,
        height: "2px",
      }}
    >
      {/* Single hairline bar in the brand accent. Linear-style: one
       * confident color, 2px tall, no shadow halo, no shimmer layer.
       * The width animates from 15% → 85% → 100% via the JS state
       * machine in this component. */}
      <Box
        sx={{
          height: "100%",
          width: `${progress}%`,
          bgcolor: "primary.main",
          transition: "all 300ms ease-out",
        }}
      />
    </Box>
  );
}
